import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import Card from '../../components/ui/Card'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { budgetAPI, categoriesAPI } from '../../services/api'
import useLanguage from '../../hooks/useLanguage'

const ExpensesPage = () => {
  const { t } = useTranslation('budget')
  const { locale } = useLanguage()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingExpense, setEditingExpense] = useState(null)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    category_id: '',
    expense_date: new Date().toISOString().split('T')[0],
    payment_method: '',
    vendor: '',
    notes: ''
  })

  const queryClient = useQueryClient()

  // Pobieranie wydatków
  const { data: expenses, isLoading, error } = useQuery(
    ['expenses'],
    async () => {
      const response = await budgetAPI.getExpenses()
      return response.data.expenses
    },
    {
      onError: (error) => {
        console.error('Błąd podczas pobierania wydatków:', error)
      }
    }
  )

  // Pobieranie kategorii do selecta
  const { data: categories } = useQuery(
    ['categories'],
    async () => {
      const response = await categoriesAPI.getCategories()
      return response.data.categories
    }
  )

  // Mutacja do tworzenia wydatku
  const createMutation = useMutation(
    async (expenseData) => {
      const response = await budgetAPI.createExpense(expenseData)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['expenses'])
        queryClient.invalidateQueries('dashboard')
        queryClient.invalidateQueries('recent-expenses')
        setIsModalOpen(false)
        resetForm()
      },
      onError: (error) => {
        console.error('Błąd podczas tworzenia wydatku:', error)
      }
    }
  )

  // Mutacja do edycji wydatku
  const updateMutation = useMutation(
    async ({ id, ...expenseData }) => {
      const response = await budgetAPI.updateExpense(id, expenseData)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['expenses'])
        queryClient.invalidateQueries('dashboard')
        queryClient.invalidateQueries('recent-expenses')
        setIsModalOpen(false)
        setEditingExpense(null)
        resetForm()
      },
      onError: (error) => {
        console.error('Błąd podczas edycji wydatku:', error)
      }
    }
  )

  // Mutacja do usuwania wydatku
  const deleteMutation = useMutation(
    async (id) => {
      await budgetAPI.deleteExpense(id)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['expenses'])
        queryClient.invalidateQueries('dashboard')
        queryClient.invalidateQueries('recent-expenses')
      },
      onError: (error) => {
        console.error('Błąd podczas usuwania wydatku:', error)
      }
    }
  )

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      amount: '',
      category_id: '',
      expense_date: new Date().toISOString().split('T')[0],
      payment_method: '',
      vendor: '',
      notes: ''
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const expenseData = {
      title: formData.title,
      amount: parseFloat(formData.amount),
      expense_date: formData.expense_date,
      // Tylko dodaj pola jeśli nie są puste
      ...(formData.description && { description: formData.description }),
      ...(formData.category_id && { category_id: formData.category_id }),
      ...(formData.payment_method && { payment_method: formData.payment_method }),
      ...(formData.vendor && { vendor: formData.vendor }),
      ...(formData.location && { location: formData.location }),
      ...(formData.notes && { notes: formData.notes })
    }

    if (editingExpense) {
      updateMutation.mutate({ id: editingExpense.id, ...expenseData })
    } else {
      createMutation.mutate(expenseData)
    }
  }

  const handleEdit = (expense) => {
    setEditingExpense(expense)
    setFormData({
      title: expense.title || '',
      description: expense.description || '',
      amount: expense.amount.toString(),
      category_id: expense.category_id || '',
      expense_date: expense.expense_date ? expense.expense_date.split('T')[0] : new Date().toISOString().split('T')[0],
      payment_method: expense.payment_method || '',
      vendor: expense.vendor || '',
      notes: expense.notes || ''
    })
    setIsModalOpen(true)
  }

  const handleDelete = (id) => {
    if (window.confirm(t('expenses.confirmDelete'))) {
      deleteMutation.mutate(id)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'PLN'
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(locale)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <Card.Body>
            <div className="text-center py-12">
              <LoadingSpinner size="lg" text={t('expenses.loading')} />
            </div>
          </Card.Body>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card>
          <Card.Body>
            <div className="text-center py-12">
              <div className="text-red-500">
                <p>{t('expenses.error')}</p>
                <p className="text-sm mt-2">{error.message}</p>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('expenses.subtitle')}
          </p>
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center rounded-md bg-[#1f9f93] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#167e75] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1f9f93]"
          >
            {t('expenses.add')}
          </button>
        </div>
      </div>

      {/* Lista wydatków */}
      <Card>
        <Card.Body>
          {expenses && expenses.length > 0 ? (
            <div className="overflow-hidden">
              <table className="min-w-full divide-y divide-[var(--color-border)]">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      {t('table.date')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      {t('table.description')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      {t('table.category')}
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      {t('table.amount')}
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wider">
                      {t('table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                  {expenses.map((expense) => (
                    <tr key={expense.id} className="hover:bg-[var(--color-hover)]">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text)]">
                        {formatDate(expense.expense_date)}
                      </td>
                      <td className="px-6 py-4 text-sm text-[var(--color-text)]">
                        <div className="font-medium">{expense.title}</div>
                        {expense.description && <div className="text-[var(--color-text-muted)] text-xs">{expense.description}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--color-text-muted)]">
                        {expense.category?.name || t('common:noCategory')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-[var(--color-text)]">
                        {formatCurrency(expense.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleEdit(expense)}
                          className="text-[#1f9f93] hover:text-[#167e75] mr-4"
                        >
                          {t('common:edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          {t('common:delete')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-[var(--color-text-muted)]">{t('expenses.noExpenses')}</p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="mt-4 inline-flex items-center rounded-md bg-[#1f9f93] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#167e75]"
              >
                {t('expenses.addFirst')}
              </button>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal dodawania/edycji wydatku */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setIsModalOpen(false)}></div>

            <div className="relative transform overflow-hidden rounded-lg bg-[var(--color-surface)] px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <form onSubmit={handleSubmit}>
                <div>
                  <div className="mt-3 text-center sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold leading-6 text-[var(--color-text)]">
                      {editingExpense ? t('expenses.editTitle') : t('expenses.addTitle')}
                    </h3>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label htmlFor="title" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('expenses.titleLabel')}
                        </label>
                        <input
                          type="text"
                          id="title"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                          placeholder={t('expenses.titlePlaceholder')}
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="description" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('expenses.descriptionLabel')}
                        </label>
                        <input
                          type="text"
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                          placeholder={t('expenses.descriptionPlaceholder')}
                        />
                      </div>

                      <div>
                        <label htmlFor="amount" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('expenses.amountLabel')}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          id="amount"
                          value={formData.amount}
                          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                          placeholder="0.00"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="expense_date" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('expenses.dateLabel')}
                        </label>
                        <input
                          type="date"
                          id="expense_date"
                          value={formData.expense_date}
                          onChange={(e) => setFormData({ ...formData, expense_date: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="category_id" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('expenses.categoryLabel')}
                        </label>
                        <select
                          id="category_id"
                          value={formData.category_id}
                          onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                        >
                          <option value="">{t('expenses.selectCategory')}</option>
                          {categories && categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="inline-flex w-full justify-center rounded-md bg-[#1f9f93] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#167e75] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1f9f93] sm:col-start-2 disabled:opacity-50"
                  >
                    {createMutation.isLoading || updateMutation.isLoading ? t('common:saving') : (editingExpense ? t('common:save') : t('common:add'))}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false)
                      setEditingExpense(null)
                      resetForm()
                    }}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-[var(--color-surface)] px-3 py-2 text-sm font-semibold text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] hover:bg-[var(--color-hover)] sm:col-start-1 sm:mt-0"
                  >
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ExpensesPage
